import {
  Form,
  DatePicker,
  Select,
  message,
  Spin,
  Button,
  Input,
  Table,
} from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { REGIONS } from '../../constants/REGIONS';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync, useToggle } from 'react-use';
import { fetchAPI } from '../../utils/api';
import { debounce } from 'lodash';
import { updateReportCount } from '../../actions/AsyncTaskReportAction';

const DownloadBulkInvoice: React.FC<{
  closeModal: () => void;
}> = props => {
  const dispatch = useDispatch();
  const layout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
  };
  const user = useSelector((state: AppState) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [form] = Form.useForm();
  const [entitySearch, setEntitySearch] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState(0);
  const [invoiceList, setInvoiceList] = useState([]);
  const [showTableList, setShowTableList] = useState(false);
  const entities = useAsync<EntityOptions[]>(async () => {
    try {
      const resp = await fetchAPI('entity/invoice_address', {
        payload: {
          q: entitySearch || '',
          is_allow_bulk_download_invoice: true,
          page: 1,
        },
      });

      return resp.items;
    } catch (error) {
      message.error('Failed get entity to download');
      return [];
    }
  }, [entitySearch]);
  const [entityInvoiceSearch, setEntityInvoiceSearch] = useState('');
  const [entityGroup, setEntityGroup] = useState('');
  const [reloadInvNum, toggleInvNumber] = useToggle(false);
  const getInvoiceNumberToDownload = async () => {
    try {
      const values = form.getFieldsValue();
      const payload = {
        ...values,
        from_date: dateRange
          ? dateRange[0]?.format('YYYY-MM-DD 00:00:00')
          : null,
        to_date: dateRange ? dateRange[1]?.format('YYYY-MM-DD 00:00:00') : null,
      };
      const resp = await fetchAPI('order/pre-download-invoice-bulk', {
        method: 'POST',
        payload: payload,
      });
      setInvoiceNumber(resp.total);
      setInvoiceList(resp.items);
      return;
    } catch (error) {
      message.error('Failed get invoice number');
    }
  };
  useEffect(() => {
    getInvoiceNumberToDownload();
  }, [reloadInvNum]); // eslint-disable-line react-hooks/exhaustive-deps
  const invoiceEntities = useAsync<EntityOptions[]>(async () => {
    try {
      const resp = await fetchAPI('entity/invoice_address', {
        payload: {
          group: entityGroup || '',
          q: entityInvoiceSearch || '',
          page: 1,
        },
      });

      return resp.items;
    } catch (error) {
      message.error('Failed get invoice entities');
      return [];
    }
  }, [entityInvoiceSearch]);
  const handleSubmit = async values => {
    const payload = {
      ...values,
      from_date: dateRange ? dateRange[0]?.format('YYYY-MM-DD 00:00:00') : null,
      to_date: dateRange ? dateRange[1]?.format('YYYY-MM-DD 00:00:00') : null,
    };
    delete payload.date_range;
    try {
      setIsSubmitting(true);
      const res = await fetchAPI(`order/invoice/bulk-download`, {
        method: 'POST',
        payload: payload,
      });
      const { counter } = await fetchAPI('reports/async-task-report/counter');
      dispatch(updateReportCount(counter));
      if (!!res.task_id) {
        message.success(`${res.message}: Report ID [${res.task_id}]`);
      } else if (!!res.file_url) {
        window.open(res.file_url, '_blank');
      }
      props.closeModal();
    } catch (error) {
      message.error(`Failed to download bulk invoice: ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  const optionsSaidStatus = [
    'PENDING INVOICE SUBMISSION',
    'INVOICE_DISPUTED',
    'PARTIALLY_PAID',
    'PAID',
    'REFUNDED',
    'PENDING PAYMENT',
  ];
  const [orderState, setOrderState] = useState({
    selected: null, // not used yet
    page: 1, // not used yet
    q: '',
  });
  const orders = useAsync(async () => {
    try {
      const resp = await fetchAPI(
        `orders/all${orderState.q && `?q=${orderState.q}`}`
      );

      return resp.items;
    } catch (error) {
      message.error('Failed get orders');
    }
  }, [orderState.q]);
  return (
    <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
      <Form {...layout} onFinish={handleSubmit} form={form}>
        <Form.Item name="date_range" label="Date">
          <React.Fragment>
            <DatePicker.RangePicker
              style={{ width: 220 }}
              onChange={(dates, dateStrings) => {
                setDateRange(dates);
                toggleInvNumber();
              }}
              // value={dateRange}
              ranges={{
                Today: [moment(), moment()],
                'This Month': [
                  moment().startOf('month'),
                  moment().endOf('month'),
                ],
                'This Year': [
                  moment().startOf('year'),
                  moment().endOf('month'),
                ],
              }}
            />
          </React.Fragment>
        </Form.Item>
        <Form.Item name="region" label="Region">
          <Select
            placeholder="Please select a region"
            onChange={value => toggleInvNumber()}
          >
            {(user.regions || []).map(regi => {
              const key = regi.trim();

              return (
                <Select.Option value={key}>
                  {`${key} (${REGIONS[key]})`}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item label="Entity to download" name="entity_to_download">
          <Select
            allowClear
            showSearch
            filterOption={false}
            placeholder="Select Entity"
            notFoundContent={entities.loading ? <Spin size="small" /> : null}
            onChange={value => {
              const selectedEntity = entities.value.filter(row => {
                return row.id === value;
              });
              setEntityGroup(
                !!selectedEntity.length ? selectedEntity[0].group : ''
              );
              toggleInvNumber();
            }}
            onSearch={debounce(value => {
              setEntitySearch(value);
            }, 500)}
          >
            {(entities.value || []).map(ent => (
              <Select.Option value={ent.id}>
                {ent.name} [ID: {ent.id}]
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="SAID Status" name="said_status">
          <Select
            mode="multiple"
            allowClear
            placeholder="Select Status(es)"
            onChange={value => toggleInvNumber()}
            options={optionsSaidStatus.map(opt => {
              return {
                value: opt,
                label: opt,
              };
            })}
          />
        </Form.Item>
        <Form.Item label="SA ID" name="ids">
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Search for order"
            notFoundContent={orders.loading ? <Spin size="small" /> : null}
            filterOption={false}
            onChange={value => toggleInvNumber()}
            onSearch={debounce(
              value => setOrderState(os => ({ ...os, q: value })),
              500
            )}
          >
            {(orders.value || []).map(order => (
              <Select.Option value={order.id}>
                SA-ID: {order.id} (PO Ref: {order.po_reference_number})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Invoice number to download">
          <span
            style={{ marginLeft: '12px', cursor: 'pointer' }}
            onClick={() =>
              !!invoiceNumber ? setShowTableList(!showTableList) : {}
            }
          >
            {invoiceNumber}{' '}
            {!!invoiceNumber ? `(click to show invoice list)` : ''}
          </span>
        </Form.Item>
        <Form.Item label="Entity in invoice" name="entity_in_invoice">
          <Select
            allowClear
            showSearch
            filterOption={false}
            placeholder="Select Entity"
            notFoundContent={
              invoiceEntities.loading ? <Spin size="small" /> : null
            }
            onChange={value => {
              const selectedEntity = entities.value.filter(row => {
                return row.id === value;
              });
              form.setFieldsValue({
                address_in_invoice: !!selectedEntity.length
                  ? selectedEntity[0].default_billing_address
                  : '',
              });
            }}
            onSearch={debounce(value => {
              setEntityInvoiceSearch(value);
            }, 500)}
          >
            {(invoiceEntities.value || []).map(ent => (
              <Select.Option value={ent.id}>
                {ent.name} [ID: {ent.id}]
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="address_in_invoice" label="Address in invoice">
          <Input.TextArea />
        </Form.Item>
        <Form.Item name="rename_file" label="Filename">
          <Input placeholder="Leave empty to use generated filename" />
        </Form.Item>
        <div
          style={{ width: '100%', display: 'flex', justifyContent: 'right' }}
        >
          <Button
            size="large"
            type="primary"
            loading={isSubmitting}
            onClick={() => form.submit()}
          >
            Download
          </Button>
        </div>
      </Form>
      {showTableList && (
        <Table
          dataSource={invoiceList}
          pagination={{
            showTotal: (totalItems, range) =>
              `${range[0]}-${range[1]} of ${totalItems} items`,
          }}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
            },
            {
              title: 'PO Reference Number',
              dataIndex: 'po_reference_number',
              key: 'po_reference_number',
            },
            {
              title: 'Address',
              dataIndex: 'address',
              key: 'address',
            },
          ]}
        />
      )}
    </div>
  );
};

export default DownloadBulkInvoice;
